import { FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, DatePicker, Form, Input, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { Installment } from '../../types/installment.types';
import { formatNumber, handleAPIError } from '../../utils/helpers';

const { RangePicker } = DatePicker;

interface FilterValues {
	device_id: number | null;
	status: string | null;
	date_range: [dayjs.Dayjs, dayjs.Dayjs] | null;
	order_by: string | null;
}

interface PaymentFormValues {
	phone_number: string;
	amount: number;
}

const Installments = () => {
	const queryClient = useQueryClient();
	const [form] = Form.useForm<FilterValues>();
	const [paginationConfig, setPaginationConfig] = useState({
		page: 1,
		pageSize: 10,
	});
	const [count, setCount] = useState(0);
	const [filters, setFilters] = useState<FilterValues>({
		device_id: null,
		status: null,
		date_range: null,
		order_by: null,
	});

	// Payment modal state
	const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
	const [selectedInstallment, setSelectedInstallment] = useState<Installment | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [paymentForm] = Form.useForm<PaymentFormValues>();

	const { data, error, isLoading } = useQuery({
		queryKey: ['installments', paginationConfig.page, paginationConfig.pageSize, filters],
		queryFn: async () => {
			const params = {
				page: paginationConfig.page,
				limit: paginationConfig.pageSize,
				device_id: filters.device_id,
				status: filters.status,
				start: filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : null,
				end: filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : null,
				order_by: filters.order_by,
			};

			const response = await axios.get('/admin/devices/installments', {
				withCredentials: true,
				params,
			});
			setCount(response.data.count);
			return response.data;
		},
	});

	if (error) {
		handleAPIError(error);
	}

	const installments: Installment[] = data?.results || [];

	const getStatusColor = (status: Installment['status']) => {
		switch (status) {
			case 'paid':
				return 'green';
			case 'pending':
				return 'blue';
			case 'overdue':
				return 'red';
			default:
				return 'default';
		}
	};

	const showPaymentModal = (installment: Installment) => {
		setSelectedInstallment(installment);
		paymentForm.resetFields();
		paymentForm.setFieldsValue({ amount: parseFloat(installment.outstanding_amount) });
		setIsPaymentModalVisible(true);
	};

	const handlePaymentCancel = () => {
		setIsPaymentModalVisible(false);
		paymentForm.resetFields();
	};

	const handleInitPayment = async (values: PaymentFormValues) => {
		if (!selectedInstallment) return;

		setIsSubmitting(true);
		try {
			await axios.post(
				`/admin/payments/installments/${selectedInstallment.id}/pay`,
				{
					phone_number: values.phone_number,
					amount: values.amount,
				},
				{
					withCredentials: true,
				},
			);

			message.success('Payment initiated successfully');
			setIsPaymentModalVisible(false);
			paymentForm.resetFields();
			queryClient.invalidateQueries({ queryKey: ['installments'] });
		} catch (err) {
			const error = err as Error;
			handleAPIError(error);
			message.error('Failed to initiate payment');
		} finally {
			setIsSubmitting(false);
		}
	};

	const columns: ColumnsType<Installment> = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: 'Device ID',
			dataIndex: 'device',
			key: 'device',
		},
		{
			title: 'Installment #',
			dataIndex: 'installment_number',
			key: 'installment_number',
			render: (number: number, installment: Installment) => (
				<div>
					<span className="font-medium">{number}</span>
					{installment.is_down_payment && (
						<Tag
							className="ml-2"
							color="purple"
						>
							Down Payment
						</Tag>
					)}
				</div>
			),
		},
		{
			title: 'Amount',
			dataIndex: 'amount',
			key: 'amount',
			render: (amount: string, installment: Installment) => {
				const paid = parseFloat(installment.paid_amount);
				const outstanding = parseFloat(installment.outstanding_amount);
				if (paid > 0 && installment.status !== 'paid') {
					return (
						<div className="flex flex-col gap-1">
							<span>{formatNumber(amount)} UGX</span>
							<Typography.Text type="secondary" className="text-xs">
								Paid: <span className="text-green-600 font-medium">{formatNumber(paid)} UGX</span>
							</Typography.Text>
							<Typography.Text type="secondary" className="text-xs">
								Outstanding: <span className="text-orange-500 font-medium">{formatNumber(outstanding)} UGX</span>
							</Typography.Text>
						</div>
					);
				}
				return <span>{formatNumber(amount)} UGX</span>;
			},
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status: string) => <Tag color={getStatusColor(status as Installment['status'])}>{status.toUpperCase()}</Tag>,
		},
		{
			title: 'Due Date',
			dataIndex: 'due_date',
			key: 'due_date',
			render: (date: Date) => dayjs(date).format('YYYY-MM-DD'),
		},
		{
			title: 'Paid At',
			dataIndex: 'paid_at',
			key: 'paid_at',
			render: (date: Date | null) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'Not Paid'),
		},
		{
			title: 'Penalty',
			dataIndex: 'penalty_amount',
			key: 'penalty_amount',
			render: (penalty: string, installment: Installment) => (installment.status === 'overdue' ? `${formatNumber(penalty)} UGX` : 'N/A'),
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (_, installment: Installment) => (
				<Button
					type="primary"
					disabled={installment.status === 'paid'}
					onClick={() => showPaymentModal(installment)}
				>
					Init Payment
				</Button>
			),
		},
	];

	const handleFilterApply = (values: FilterValues) => {
		setFilters(values);
		setPaginationConfig({
			...paginationConfig,
			page: 1, // Reset to first page when filtering
		});
	};

	const handleFilterReset = () => {
		form.resetFields();
		setFilters({
			device_id: null,
			status: null,
			date_range: null,
			order_by: null,
		});
		setPaginationConfig({
			...paginationConfig,
			page: 1,
		});
	};

	const statusOptions = [
		{ value: 'paid', label: 'Paid' },
		{ value: 'pending', label: 'Pending' },
		{ value: 'overdue', label: 'Overdue' },
	];

	const orderByOptions = [
		{ value: 'installment_number', label: 'Installment Number' },
		{ value: 'due_date', label: 'Due Date' },
		{ value: 'amount', label: 'Amount' },
		{ value: 'status', label: 'Status' },
	];

	return (
		<AppLayout
			title="Installments"
			subtitle="Manage device installments and payment status."
		>
			<Card className="mb-4 shadow-sm">
				<Form
					form={form}
					layout="horizontal"
					onFinish={handleFilterApply}
					initialValues={{
						...filters,
						date_range: filters.date_range,
					}}
				>
					<div className="flex flex-wrap items-end gap-4">
						<Form.Item
							name="device_id"
							label="Device ID"
							className="mb-0 min-w-[150px]"
						>
							<Input
								placeholder="Enter device ID"
								type="number"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="status"
							label="Status"
							className="mb-0 min-w-[150px]"
						>
							<Select
								placeholder="Select status"
								allowClear
								options={statusOptions}
							/>
						</Form.Item>

						<Form.Item
							name="date_range"
							label="Due Date Range"
							className="mb-0 min-w-[300px]"
						>
							<RangePicker
								allowClear
								format="YYYY-MM-DD"
								placeholder={['Start Date', 'End Date']}
							/>
						</Form.Item>

						<Form.Item
							name="order_by"
							label="Order By"
							className="mb-0 min-w-[200px]"
						>
							<Select
								placeholder="Select field to order by"
								allowClear
								options={orderByOptions}
							/>
						</Form.Item>

						<Form.Item className="mb-0 flex-shrink-0">
							<Space>
								<Button
									type="primary"
									htmlType="submit"
									icon={<FilterOutlined />}
								>
									Apply Filters
								</Button>
								<Button
									onClick={handleFilterReset}
									icon={<ReloadOutlined />}
								>
									Reset
								</Button>
							</Space>
						</Form.Item>
					</div>
				</Form>
			</Card>

			<div className="mb-20">
				<Table
					columns={columns}
					dataSource={installments}
					rowKey="id"
					pagination={{
						total: count,
						pageSize: paginationConfig.pageSize,
						current: paginationConfig.page,
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
						onChange: (page, pageSize) => {
							setPaginationConfig({ page, pageSize });
						},
					}}
					loading={isLoading}
				/>
			</div>

			<Modal
				title="Send Payment Prompt"
				open={isPaymentModalVisible}
				onCancel={handlePaymentCancel}
				footer={null}
			>
				<Form
					form={paymentForm}
					layout="vertical"
					onFinish={handleInitPayment}
				>
					{selectedInstallment && (
						<div className="mb-4 rounded-lg bg-gray-50 p-3">
							<Typography.Text type="secondary">
								Outstanding: <strong>{parseFloat(selectedInstallment.outstanding_amount).toLocaleString()} UGX</strong>
							</Typography.Text>
						</div>
					)}
					<Form.Item
						name="phone_number"
						label="Phone Number"
						tooltip="The phone number of the user to send the payment prompt to. Make sure to include the country code."
						rules={[
							{ required: true, message: 'Please enter the phone number' },
							{ pattern: /^[0-9+]+$/, message: 'Please enter a valid phone number' },
						]}
					>
						<Input placeholder="Enter phone number" />
					</Form.Item>
					<Form.Item
						name="amount"
						label="Amount"
						tooltip="Enter the amount to pay. Cannot exceed the outstanding balance."
						rules={[
							{ required: true, message: 'Please enter an amount' },
							{
								validator: (_, value) => {
									if (!selectedInstallment) return Promise.resolve();
									const outstanding = parseFloat(selectedInstallment.outstanding_amount);
									if (value < 500) return Promise.reject(new Error('Minimum amount is UGX 500'));
									if (value > outstanding) return Promise.reject(new Error(`Amount cannot exceed ${outstanding.toLocaleString()} UGX`));
									return Promise.resolve();
								},
							},
						]}
					>
						<Input type="number" suffix="UGX" placeholder="Enter amount" />
					</Form.Item>
					<div className="flex justify-end gap-2">
						<Button onClick={handlePaymentCancel}>Cancel</Button>
						<Button
							type="primary"
							htmlType="submit"
							loading={isSubmitting}
						>
							Send
						</Button>
					</div>
				</Form>
			</Modal>
		</AppLayout>
	);
};

export default Installments;
